import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {getFeedRecords, createFeedRecord, updateFeedRecord, fetchPond, updateCycle, PondCycle} from "../../api/pond";
import {fetchFeeds, fetchTemplateNames, fetchAllTemplates} from "../../api/feed";
import {fetchMarketPriceAreas, fetchMarketPriceCommodities, MarketPriceArea, MarketPriceCommodity} from "../../api/marketPrice";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import {useTranslation} from "react-i18next";

interface FeedTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
  cycleStartDate?: string;
  selectedCycle?: PondCycle | null;
  onCycleUpdated?: () => void | Promise<void>;
}

const FeedTab: React.FC<FeedTabProps> = ({
  pondId,
  cycleId,
  isCycleActive = true,
  cycleStartDate,
  selectedCycle,
  onCycleUpdated,
}) => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [feedRecords, setFeedRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [feeds, setFeeds] = useState<any[]>([]);
  const [templateNames, setTemplateNames] = useState<any[]>([]);
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [selectedFeed, setSelectedFeed] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [currentDOC, setCurrentDOC] = useState<number>(0);
  const [predictedFCR, setPredictedFCR] = useState<number | null>(null);
  const [pondDetails, setPondDetails] = useState<any>(null);
  const [stockingDialogOpen, setStockingDialogOpen] = useState(false);
  const [stockingSaving, setStockingSaving] = useState(false);
  const [stockingError, setStockingError] = useState<string | null>(null);
  const [marketAreas, setMarketAreas] = useState<MarketPriceArea[]>([]);
  const [marketCommodities, setMarketCommodities] = useState<MarketPriceCommodity[]>([]);
  const [stockingForm, setStockingForm] = useState({
    initialStockPricePerKg: "",
    feedPricePerKg: "",
    medicineCost: "",
    powerBills: "",
    salaries: "",
    otherProductionCosts: "",
    marketAreaId: "",
    marketCommodityId: "",
    marketLabel: "",
  });

  const form = useForm({
    initialValues: {
      feedDate: "",
      feed1: 0,
      feed2: 0,
      feed3: 0,
      feed4: 0,
      remark: "",
      feedId: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.FEED_RECORD,
    validationContext: {
      cycleStartDate: cycleStartDate,
    },
    onSubmit: async (values) => {
      try {
        // Check if there's a selected cycle before allowing feed record creation
        if (!cycleId) {
          setError("No cycle selected. Please select a cycle first.");
          return;
        }

        if (!isCycleActive) {
          setError("Cannot add feed records to a closed cycle.");
          return;
        }

        // Convert feed values to numbers and ensure they are valid
        const feedData = {
          ...values,
          feed1: Number(values.feed1) || 0,
          feed2: Number(values.feed2) || 0,
          feed3: Number(values.feed3) || 0,
          feed4: Number(values.feed4) || 0,
          // Use the pond's feed type automatically
          feedType: pondDetails?.feedType || "",
        };

        if (editingRecord) {
          await updateFeedRecord(editingRecord.id, feedData);
        } else {
          await createFeedRecord(pondId, feedData);
        }
        setModalOpen(false);
        setError(null);
        fetchFeedRecords();
      } catch (err: any) {
        console.error("Feed record save error:", err);
        setError(err.response?.data?.message || "Failed to save feed record");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  useEffect(() => {
    fetchFeedRecords();
    loadTemplateNames();
    loadPondDetails();
    loadFeeds(); // Load feeds for all users, backend handles tenant filtering
  }, [pondId, cycleId, user?.role]);

  // Update validation context when cycleStartDate changes
  useEffect(() => {
    form.updateValidationContext({
      cycleStartDate: cycleStartDate,
    });
    // Force re-validation when cycle changes
    if (form.values.feedDate) {
      form.validateField("feedDate");
    }
  }, [cycleStartDate]);

  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  const loadMarketMasters = async () => {
    try {
      const [areas, commodities] = await Promise.all([
        fetchMarketPriceAreas(!isSuperAdmin),
        fetchMarketPriceCommodities(!isSuperAdmin),
      ]);
      setMarketAreas(areas);
      setMarketCommodities(commodities);
    } catch (e) {
      console.error("Failed to load market masters", e);
    }
  };

  useEffect(() => {
    loadMarketMasters();
  }, [isSuperAdmin]);

  const hasInitialStockingPrice =
    selectedCycle?.initialStockPricePerKg != null && Number(selectedCycle.initialStockPricePerKg) > 0;

  const openStockingDialog = () => {
    setStockingError(null);
    const preferredShrimp = marketCommodities.find((c) => c.isActive && c.name.toLowerCase() === "shrimp");
    const defaultCommodity = preferredShrimp || marketCommodities.find((c) => c.isActive);
    const defaultArea = marketAreas.find((a) => a.isActive);
    setStockingForm({
      initialStockPricePerKg:
        selectedCycle?.initialStockPricePerKg != null ? String(selectedCycle.initialStockPricePerKg) : "",
      feedPricePerKg: selectedCycle?.feedPricePerKg != null ? String(selectedCycle.feedPricePerKg) : "",
      medicineCost: selectedCycle?.medicineCost != null ? String(selectedCycle.medicineCost) : "",
      powerBills: selectedCycle?.powerBills != null ? String(selectedCycle.powerBills) : "",
      salaries: selectedCycle?.salaries != null ? String(selectedCycle.salaries) : "",
      otherProductionCosts: selectedCycle?.otherProductionCosts != null ? String(selectedCycle.otherProductionCosts) : "",
      marketAreaId: selectedCycle?.marketAreaId || defaultArea?.id || "",
      marketCommodityId: selectedCycle?.marketCommodityId || defaultCommodity?.id || "",
      marketLabel: selectedCycle?.marketLabel || "",
    });
    setStockingDialogOpen(true);
  };

  const saveStockingMeta = async () => {
    if (!cycleId) return;
    const price = Number(stockingForm.initialStockPricePerKg);
    if (!price || price < 0) {
      setStockingError("Enter a valid initial stocking price (INR per animal).");
      return;
    }
    if (!stockingForm.marketAreaId || !stockingForm.marketCommodityId) {
      setStockingError("Select market area and commodity.");
      return;
    }
    const commodity = marketCommodities.find((c) => c.id === stockingForm.marketCommodityId);
    const isShrimp = (commodity?.name || "").toLowerCase().includes("shrimp");
    if (isShrimp && !stockingForm.marketLabel.trim()) {
      setStockingError("Shrimp type (e.g. Vannamei) is required for market price matching.");
      return;
    }
    const feedRaw = stockingForm.feedPricePerKg.trim();
    let feedPricePerKg: number | null | undefined = undefined;
    if (feedRaw === "") {
      feedPricePerKg = null;
    } else {
      const f = Number(feedRaw);
      if (Number.isNaN(f) || f < 0) {
        setStockingError("Enter a valid feed price (₹ per kg) or leave blank.");
        return;
      }
      feedPricePerKg = f;
    }
    const parseOptionalInr = (raw: string): number | null | "invalid" => {
      const trimmed = raw.trim();
      if (trimmed === "") return null;
      const n = Number(trimmed);
      if (Number.isNaN(n) || n < 0) return "invalid";
      return n;
    };
    const medicineCost = parseOptionalInr(stockingForm.medicineCost);
    const powerBills = parseOptionalInr(stockingForm.powerBills);
    const salaries = parseOptionalInr(stockingForm.salaries);
    const otherProductionCosts = parseOptionalInr(stockingForm.otherProductionCosts);
    if (
      medicineCost === "invalid" ||
      powerBills === "invalid" ||
      salaries === "invalid" ||
      otherProductionCosts === "invalid"
    ) {
      setStockingError(
        t("optionalInrCostsInvalid", {
          defaultValue: "Optional costs must be valid non-negative INR amounts or left blank.",
        }),
      );
      return;
    }
    setStockingSaving(true);
    setStockingError(null);
    try {
      await updateCycle(cycleId, {
        initialStockPricePerKg: price,
        feedPricePerKg,
        medicineCost,
        powerBills,
        salaries,
        otherProductionCosts,
        marketAreaId: stockingForm.marketAreaId,
        marketCommodityId: stockingForm.marketCommodityId,
        marketLabel: stockingForm.marketLabel.trim() || undefined,
      });
      setStockingDialogOpen(false);
      await onCycleUpdated?.();
    } catch (err: any) {
      setStockingError(err.response?.data?.message || err.message || "Failed to save stocking price");
    } finally {
      setStockingSaving(false);
    }
  };

  const fetchFeedRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFeedRecords(pondId, cycleId);
      setFeedRecords(data.data || data);
    } catch (err) {
      setError("Failed to load feed records");
    } finally {
      setLoading(false);
    }
  };

  const loadFeeds = async () => {
    try {
      console.log("Loading feeds...");
      const feedsData = await fetchFeeds();
      console.log("Feeds loaded:", feedsData);
      setFeeds(feedsData);
    } catch (err) {
      console.error("Failed to load feeds:", err);
      setFeeds([]); // Ensure feeds is set to empty array on error
    }
  };

  const loadTemplateNames = async () => {
    try {
      const templateNamesData = await fetchTemplateNames();
      const allTemplatesData = await fetchAllTemplates();
      setTemplateNames(templateNamesData);
      setAllTemplates(allTemplatesData);
    } catch (err) {
      console.error("Failed to load template names:", err);
    }
  };

  const loadPondDetails = async () => {
    try {
      const pondData = await fetchPond(pondId);
      setPondDetails(pondData);
      // Set the selected template to the pond's feed type
      if (pondData.feedType) {
        setSelectedTemplate(pondData.feedType);
      }
    } catch (err) {
      console.error("Failed to load pond details:", err);
    }
  };

  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      form.setValues({
        feedDate: record.feedDate ? new Date(record.feedDate).toISOString().split("T")[0] : "",
        feed1: record.feed1 || 0,
        feed2: record.feed2 || 0,
        feed3: record.feed3 || 0,
        feed4: record.feed4 || 0,
        remark: record.remark || "",
        feedId: record.feedId || "",
      });
    } else {
      setEditingRecord(null);
      form.setValues({
        feedDate: new Date().toISOString().split("T")[0],
        feed1: 0,
        feed2: 0,
        feed3: 0,
        feed4: 0,
        remark: "",
        feedId: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.reset();
  };

  const calculateTotal = () => {
    const feed1 = Number(form.values.feed1) || 0;
    const feed2 = Number(form.values.feed2) || 0;
    const feed3 = Number(form.values.feed3) || 0;
    const feed4 = Number(form.values.feed4) || 0;
    return feed1 + feed2 + feed3 + feed4;
  };

  // Calculate predicted FCR based on selected template and DOC
  const calculatePredictedFCR = () => {
    if (selectedTemplate && currentDOC > 0) {
      const template = allTemplates.find((t) => t.templateName === selectedTemplate && t.doc === currentDOC);
      return template?.fcrPercentage || null;
    }
    return null;
  };

  // Update predicted FCR when template or DOC changes
  React.useEffect(() => {
    const predicted = calculatePredictedFCR();
    setPredictedFCR(predicted);
  }, [selectedTemplate, currentDOC, allTemplates]);

  // Helper to get date-only (ignoring time)
  function getDateOnly(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const selectedCommodityForStocking = marketCommodities.find((c) => c.id === stockingForm.marketCommodityId);
  const isShrimpCommodityForForm = (selectedCommodityForStocking?.name || "").toLowerCase().includes("shrimp");

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">{t("Feed Records")}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {cycleId && !hasInitialStockingPrice && isCycleActive && (
            <Button variant="outlined" color="secondary" onClick={openStockingDialog}>
              {t("Add Stocking Price")}
            </Button>
          )}
          {cycleId && hasInitialStockingPrice && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t("Stocking price")}: ₹{Number(selectedCycle?.initialStockPricePerKg).toFixed(4)}{" "}
                {t("per animal")}
                {selectedCycle?.initialStockPriceCapturedAt
                  ? ` · ${new Date(selectedCycle.initialStockPriceCapturedAt).toLocaleString()}`
                  : ""}
                {selectedCycle?.feedPricePerKg != null && Number(selectedCycle.feedPricePerKg) > 0 ? (
                  <>
                    {" · "}
                    {t("Feed price")}: ₹{Number(selectedCycle.feedPricePerKg).toFixed(2)}/{t("kg")}
                  </>
                ) : null}
              </Typography>
              {isCycleActive && (
                <Button size="small" variant="outlined" onClick={openStockingDialog}>
                  {t("Edit")}
                </Button>
              )}
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal()}
            disabled={!cycleId || !isCycleActive}
            title={!cycleId ? "No cycle selected" : !isCycleActive ? "Cannot add records to closed cycles" : ""}
          >
            {t("Add Feed Record")}
          </Button>
        </Box>
      </Box>

      {!cycleId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("No cycle selected for this pond. Please select a cycle from the dropdown above.")}
        </Alert>
      )}
      {cycleId && !isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}


      {/* Feed Selection Dropdown */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          User: {user?.name} ({user?.role}) | Feeds: {feeds.length}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>{t("Select Feed")}</InputLabel>
          <Select value={selectedFeed} onChange={(e) => setSelectedFeed(e.target.value)} label={t("Select Feed")}>
            <MenuItem value="">
              <em>{t("All Feeds")}</em>
            </MenuItem>
            {feeds.map((feed) => (
              <MenuItem key={feed.id} value={feed.id}>
                {feed.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} sx={{overflowX: "auto", maxWidth: "100%"}}>
        <Table size="small" sx={{minWidth: 500}}>
          <TableHead>
            <TableRow>
              <TableCell align="center">{t("DOC")}</TableCell>
              <TableCell align="center">{t("Date")}</TableCell>
              <TableCell>{t("Feed 1 (kg)")}</TableCell>
              <TableCell>{t("Feed 2 (kg)")}</TableCell>
              <TableCell>{t("Feed 3 (kg)")}</TableCell>
              <TableCell>{t("Feed 4 (kg)")}</TableCell>
              <TableCell>{t("Total (kg)")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Cumulative Feed")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedRecords.map((record, idx) => {
              // Calculate DOC for each feed record using date-only
              let doc = "-";
              if (cycleStartDate && record.feedDate) {
                const feedDate = getDateOnly(new Date(record.feedDate));
                const startDate = getDateOnly(new Date(cycleStartDate));
                const calculatedDoc = Math.floor((feedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                doc = calculatedDoc.toString();

                // Debug logging
                console.log("DOC calculation:", {
                  feedDate: record.feedDate,
                  cycleStartedAt: cycleStartDate,
                  feedDateOnly: feedDate,
                  startDateOnly: startDate,
                  doc: calculatedDoc,
                });
              } else {
                console.log("Missing data for DOC calculation:", {
                  hasCycleId: !!cycleId,
                  hasFeedDate: !!record.feedDate,
                  hasStartedAt: !!cycleStartDate,
                });
              }
              return (
                <TableRow key={record.id}>
                  <TableCell align="center">{doc}</TableCell>
                  <TableCell align="center">{record.feedDate ? new Date(record.feedDate).toLocaleDateString() : "-"}</TableCell>
                  <TableCell>{record.feed1 || 0}</TableCell>
                  <TableCell>{record.feed2 || 0}</TableCell>
                  <TableCell>{record.feed3 || 0}</TableCell>
                  <TableCell>{record.feed4 || 0}</TableCell>
                  <TableCell>{record.totalQuantity || 0}</TableCell>
                  <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{record.remark || ""}</TableCell>
                  <TableCell>
                    {record.cumulativeFeed
                      ? typeof record.cumulativeFeed === "number"
                        ? record.cumulativeFeed.toFixed(2)
                        : parseFloat(record.cumulativeFeed).toFixed(2)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleOpenModal(record)} disabled={!isCycleActive}>
                      {t("Edit")}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={stockingDialogOpen} onClose={() => !stockingSaving && setStockingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{hasInitialStockingPrice ? t("Edit Stocking Price") : t("Add Stocking Price")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {stockingError && (
              <Alert severity="error" onClose={() => setStockingError(null)}>
                {stockingError}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {t("Enter cost per stocked animal (post-larva) in INR. Total initial value = stocking count × this price.")}
            </Typography>
            <TextField
              label={t("Initial price (₹ per animal)")}
              type="number"
              value={stockingForm.initialStockPricePerKg}
              onChange={(e) => setStockingForm((s) => ({...s, initialStockPricePerKg: e.target.value}))}
              inputProps={{min: 0, step: "0.0001"}}
              fullWidth
              required
            />
            <TextField
              label={t("Feed price (₹ per kg)")}
              type="number"
              value={stockingForm.feedPricePerKg}
              onChange={(e) => setStockingForm((s) => ({...s, feedPricePerKg: e.target.value}))}
              inputProps={{min: 0, step: "0.01"}}
              fullWidth
              helperText={t("Optional. Used to estimate total feed cost for this cycle.")}
            />
            <Typography variant="subtitle2" color="text.secondary">
              {t("productionCostsSection", {
                defaultValue: "Other production costs (optional, INR for this cycle)",
              })}
            </Typography>
            <TextField
              label={t("Medicine cost")}
              type="number"
              value={stockingForm.medicineCost}
              onChange={(e) => setStockingForm((s) => ({...s, medicineCost: e.target.value}))}
              inputProps={{min: 0, step: "0.01"}}
              fullWidth
            />
            <TextField
              label={t("Power bills")}
              type="number"
              value={stockingForm.powerBills}
              onChange={(e) => setStockingForm((s) => ({...s, powerBills: e.target.value}))}
              inputProps={{min: 0, step: "0.01"}}
              fullWidth
            />
            <TextField
              label={t("Salaries")}
              type="number"
              value={stockingForm.salaries}
              onChange={(e) => setStockingForm((s) => ({...s, salaries: e.target.value}))}
              inputProps={{min: 0, step: "0.01"}}
              fullWidth
            />
            <TextField
              label={t("Other production costs")}
              type="number"
              value={stockingForm.otherProductionCosts}
              onChange={(e) => setStockingForm((s) => ({...s, otherProductionCosts: e.target.value}))}
              inputProps={{min: 0, step: "0.01"}}
              fullWidth
              helperText={t("otherProductionCostsHint", {defaultValue: "Medicine, power, salaries, misc. — included in harvest-now P/L."})}
            />
            <FormControl fullWidth required>
              <InputLabel>{t("Market area")}</InputLabel>
              <Select
                label={t("Market area")}
                value={stockingForm.marketAreaId}
                onChange={(e) => setStockingForm((s) => ({...s, marketAreaId: e.target.value}))}
              >
                {marketAreas.filter((a) => a.isActive).map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{t("Commodity")}</InputLabel>
              <Select
                label={t("Commodity")}
                value={stockingForm.marketCommodityId}
                onChange={(e) => setStockingForm((s) => ({...s, marketCommodityId: e.target.value}))}
              >
                {marketCommodities.filter((c) => c.isActive).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isShrimpCommodityForForm && (
              <TextField
                label={t("Shrimp type (e.g. Vannamei)")}
                value={stockingForm.marketLabel}
                onChange={(e) => setStockingForm((s) => ({...s, marketLabel: e.target.value}))}
                fullWidth
                required
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockingDialogOpen(false)} disabled={stockingSaving}>
            {t("Cancel")}
          </Button>
          <Button onClick={saveStockingMeta} variant="contained" disabled={stockingSaving}>
            {stockingSaving ? <CircularProgress size={20} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Feed Record") : t("Add Feed Record")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormField
              label={t("Date")}
              name="feedDate"
              type="date"
              value={form.values.feedDate}
              onChange={form.handleChange("feedDate")}
              error={form.errors.feedDate}
            />

            {/* DOC Input for FCR Prediction */}
            <TextField
              label={t("Current DOC (Days of Culture)")}
              type="number"
              value={currentDOC}
              onChange={(e) => setCurrentDOC(Number(e.target.value))}
              InputProps={{inputProps: {min: 0}}}
              helperText={t("Enter current DOC for FCR prediction")}
            />

            {/* FCR Prediction Display */}
            {predictedFCR !== null && (
              <Alert severity="info">
                <Typography variant="subtitle2">
                  {t("Predicted FCR for {{template}} at DOC {{doc}}:", {template: selectedTemplate, doc: currentDOC})}{" "}
                  <strong>{predictedFCR.toFixed(3)}</strong>
                </Typography>
              </Alert>
            )}

            {/* Feed Selection */}
            <FormControl fullWidth>
              <InputLabel>{t("Feed ID")}</InputLabel>
              <Select
                value={form.values.feedId}
                onChange={(e) => form.setValues({...form.values, feedId: e.target.value})}
                label={t("Feed ID")}
                error={!!form.errors.feedId}>
                {feeds.length === 0 && (
                  <MenuItem disabled>
                    <em>No feeds available</em>
                  </MenuItem>
                )}
                {feeds.map((feed) => (
                  <MenuItem key={feed.id} value={feed.id}>
                    {feed.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormField
              label={t("Feed 1 (kg)")}
              name="feed1"
              type="number"
              value={form.values.feed1}
              onChange={form.handleChange("feed1")}
              error={form.errors.feed1}
              min={0}
              step={0.01}
            />
            <FormField
              label={t("Feed 2 (kg)")}
              name="feed2"
              type="number"
              value={form.values.feed2}
              onChange={form.handleChange("feed2")}
              error={form.errors.feed2}
              min={0}
              step={0.01}
            />
            <FormField
              label={t("Feed 3 (kg)")}
              name="feed3"
              type="number"
              value={form.values.feed3}
              onChange={form.handleChange("feed3")}
              error={form.errors.feed3}
              min={0}
              step={0.01}
            />
            <FormField
              label={t("Feed 4 (kg)")}
              name="feed4"
              type="number"
              value={form.values.feed4}
              onChange={form.handleChange("feed4")}
              error={form.errors.feed4}
              min={0}
              step={0.01}
            />
            <TextField
              label={t("Total Quantity (kg)")}
              type="number"
              value={calculateTotal().toFixed(2)}
              InputProps={{readOnly: true}}
              sx={{backgroundColor: "#f5f5f5"}}
            />
            <FormField
              label={t("Remark")}
              name="remark"
              type="textarea"
              multiline
              rows={3}
              value={form.values.remark}
              onChange={form.handleChange("remark")}
              error={form.errors.remark}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={form.handleSubmit} variant="contained" disabled={form.isSubmitting}>
            {form.isSubmitting ? <CircularProgress size={20} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedTab;
